import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { IssueTrackerService, TrackerIssue } from '../../services/issueTracker.service';

@Component({
    selector: 'app-issue-tracker',
    templateUrl: './issue-tracker.component.html',
    styles: [`
      /* The one gold in the application. --gold-500 is the design system's
         highlight, reserved for emphasis; --gold-600 keeps the text readable
         against white, which the 500 does not at this size. */
      .priority-high {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--gold-600);
        font-weight: var(--weight-semibold);
        text-transform: capitalize;
      }
      .priority-high lucide-icon {
        color: var(--gold-500);
      }
    `],
    standalone: false,
})
export class IssueTrackerComponent implements OnInit {
    @ViewChild('reportDialog') reportDialog: TemplateRef<any>;

    issues: TrackerIssue[] = [];
    loading = false;
    /** Off by default: the list is for finding out whether a problem is known. */
    includeClosed = false;
    /**
     * Whether an issue is worth marking. The tracker's own vocabulary is
     * free text, so this matches what the report dialog can send ('high') and
     * the words a tracker is likely to use beside it, case-insensitively --
     * rather than assuming one spelling and silently marking nothing.
     */
    isHighPriority(issue: any): boolean {
        const p = String(issue?.priority || '').toLowerCase();
        return p === 'high' || p === 'urgent' || p === 'critical' || p === 'blocker';
    }

    /** A machine code from the server, translated in the template. */
    errorCode: string | null = null;

    title = '';
    description = '';
    priority = 'medium';
    submitting = false;

    constructor(
        private issueTracker: IssueTrackerService,
        private modalService: NgbModal,
        private alertService: ToastrService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.loading = true;
        this.errorCode = null;
        this.issueTracker.list(this.includeClosed).subscribe({
            next: (data) => {
                this.issues = (data && data.issues) || [];
                this.loading = false;
            },
            error: (err) => {
                // The server answers a machine code; anything else means it did not
                // answer at all. Never show the tracker's own error text: it is
                // another system's, and a key problem is not the reader's to fix.
                this.errorCode = (err && err.error && err.error.error) || 'TRACKER_UNAVAILABLE';
                this.issues = [];
                this.loading = false;
            },
        });
    }

    toggleClosed(): void {
        this.includeClosed = !this.includeClosed;
        this.load();
    }

    isClosed(issue: TrackerIssue): boolean {
        // Mirrors the server's rule, and for the same reason: an unfamiliar
        // status reads as open rather than being hidden or mislabelled.
        const status = (issue && issue.status ? issue.status : '').toLowerCase().replace(/[^a-z]/g, '');
        return ['closed', 'resolved', 'done', 'rejected', 'wontfix', 'duplicate'].indexOf(status) !== -1;
    }

    openReport(): void {
        this.title = '';
        this.description = '';
        this.priority = 'medium';
        this.modalService.open(this.reportDialog, { size: 'lg', centered: true, backdrop: 'static' });
    }

    submitReport(modal: any): void {
        if (!this.title.trim() || !this.description.trim() || this.submitting) return;
        this.submitting = true;

        this.issueTracker.create(this.title.trim(), this.description.trim(), this.priority).subscribe({
            next: () => {
                this.submitting = false;
                modal.close();
                this.alertService.success(this.translate.instant('ISSUE.REPORTED'));
                // Reload rather than pushing the new issue in: the tracker assigns
                // the id, status and timestamps, and guessing them here would show
                // a row that does not match what it stored.
                this.load();
            },
            error: (err) => {
                this.submitting = false;
                const code = (err && err.error && err.error.error) || 'TRACKER_UNAVAILABLE';
                this.alertService.error(this.translate.instant('ISSUE.ERROR.' + code));
            },
        });
    }
}
